import * as React from 'react';
import dateUtil from '../../../utils/dateUtil';
import { getAbsenceTypeLabel } from './weeklyShiftsUtils';
import styles from './WeeklyShifts.module.css';

interface AbsModalState {
  open: boolean;
  date?: string;
  type?: 'red' | 'orange';
  anchor?: { top: number; left: number };
}

interface WeeklyShiftsAbsenceModalProps {
  absModal: AbsModalState;
  setAbsModal: (v: any) => void;
  weekAbsences: any[];
  allEmployees: any[];
}

export function WeeklyShiftsAbsenceModal({
  absModal,
  setAbsModal,
  weekAbsences,
  allEmployees,
}: WeeklyShiftsAbsenceModalProps): React.JSX.Element | null {
  if (!absModal.open || !absModal.date) return null;

  const dayISO = absModal.date!;
  const ds = new Date(`${dayISO}T00:00:00`).getTime();
  const de = new Date(`${dayISO}T23:59:59`).getTime();
  const items = weekAbsences.filter((a: any) => {
    const as2 = dateUtil.createLocalDate(a.start_datetime).getTime();
    const ae = dateUtil.createLocalDate(a.end_datetime).getTime();
    return as2 <= de && ae > ds;
  });
  const approved = items.filter((a: any) => (a.status || '').toUpperCase() === 'APPROVED');
  const approvedEmployeeIds = new Set(approved.map((a: any) => a.employee));
  const requestedRaw = items.filter(
    (a: any) => (a.status || '').toUpperCase() === 'REQUESTED' && !approvedEmployeeIds.has(a.employee)
  );
  const seenRequestedEmployees = new Set<number>();
  const requested = requestedRaw.filter((a: any) => {
    if (seenRequestedEmployees.has(a.employee)) return false;
    seenRequestedEmployees.add(a.employee);
    return true;
  });

  const formatAbsencePeriod = (a: any) => {
    const s = dateUtil.createLocalDate(a.start_datetime);
    const e = dateUtil.createLocalDate(a.end_datetime);
    const fmt = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
    return `${fmt(s)} \u2013 ${fmt(e)}`;
  };

  const renderItems = (list: any[], type: 'approved' | 'requested') => (
    <div className={styles.absItemList}>
      {list.map((a: any) => {
        const emp = allEmployees.find((e: any) => e.id === a.employee);
        const name = emp ? `${emp.first_name} ${emp.last_name}` : `#${a.employee}`;
        return (
          <div
            key={a.id}
            className={type === 'approved' ? styles.absItemApproved : styles.absItemRequested}
          >
            <div className={styles.absItemName}>{name}</div>
            <div className={styles.absItemDetails}>
              <span className={styles.absItemPeriod}>{formatAbsencePeriod(a)}</span>
              <span
                className={
                  type === 'approved' ? styles.absTypeBadgeApproved : styles.absTypeBadgeRequested
                }
              >
                {getAbsenceTypeLabel((a as any).absence_type)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className={styles.absModalBackdrop} onClick={() => setAbsModal({ open: false })} />
      <div
        className={styles.absModalPanel}
        style={{
          left: absModal.anchor?.left ?? '50%',
          top: absModal.anchor?.top ?? '50%',
          transform: absModal.anchor ? 'none' : 'translate(-50%, -50%)',
        }}
        onMouseEnter={() => setAbsModal((prev: any) => ({ ...prev, open: true }))}
        onMouseLeave={() => setAbsModal({ open: false })}
      >
        <div className={styles.absModalTitle}>Absences</div>
        {items.length === 0 && <div className={styles.absModalEmpty}>No entries</div>}
        {approved.length > 0 && (
          <div style={{ marginBottom: requested.length > 0 ? '14px' : 0 }}>
            <div className={`${styles.absSectionLabel} ${styles.absSectionLabelApproved}`}>
              Approved
            </div>
            {renderItems(approved, 'approved')}
          </div>
        )}
        {requested.length > 0 && (
          <div>
            <div className={`${styles.absSectionLabel} ${styles.absSectionLabelRequested}`}>
              Requested
            </div>
            {renderItems(requested, 'requested')}
          </div>
        )}
      </div>
    </>
  );
}
